import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Container,
  Grid,
  Box,
  Typography,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Print,
  Download,
  Phone as PhoneIcon,
  MailOutline as MailOutlineIcon,
  LocationOn as LocationOnIcon,
  Circle as CircleIcon,
  Square as SquareIcon,
  DragIndicator,
  Reorder,
  LinkedIn as LinkedInIcon
} from "@mui/icons-material";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import LanguageIcon from '@mui/icons-material/Language';

export const resumeMeta = {
  hasPhoto: true,
  columns: 2,
};

// A4 page size in mm and px (96dpi)
const pageSizeMap = {
  A4: { width: "210mm", height: "297mm", widthPx: 794, heightPx: 1123 },
  Letter: { width: "216mm", height: "279mm", widthPx: 816, heightPx: 1056 },
  Legal: { width: "216mm", height: "356mm", widthPx: 816, heightPx: 1346 },
  A3: { width: "297mm", height: "420mm", widthPx: 1123, heightPx: 1587 },
  Executive: { width: "184mm", height: "267mm", widthPx: 695, heightPx: 1009 },
};

// --- Styling Constants ---
const darkBlue = '#0A3870';
const lightGrayText = '#ccc';
const secondaryText = '#666';

const defaultData = {
  firstName: "JONATHAN",
  lastName: "PATTERSON",
  currentPosition: "Marketing Manager",
  phone: "+123-456-7890",
  email: "hello@reallygreatsite.com",
  city: "123 Anywhere St., Any City",
  website: "www.reallygreatsite.com",
  profile: "As a marketing manager, I have had the privilege of working with a diverse range of clients across various industries. I have developed marketing methodologies, started 13 new established organizations, and have consistently delivered results that exceeded their expectations.",
  skills: [
    "Management Skills", "Creativity", "Digital Marketing", "Negotiation",
    "Critical Thinking", "Leadership"
  ],
  references: [
    {
      name: "Harumi Kobayashi",
      company: "Salford & Co.",
      position: "CEO",
      phone: "123-456-7890",
      email: "hello@reallygreatsite.com",
    },
    {
      name: "Bailey Dupont",
      company: "Arrowwai Industries",
      position: "CEO",
      phone: "123-456-7890",
      email: "hello@reallygreatsite.com",
    },
  ],
};

// Separate section orders for left and right sides
const defaultLeftSectionOrder = [
  'aboutMe',
  'contact',
  'skills',
  'languages',
  'interests'
];

const defaultRightSectionOrder = [
  'experience',
  'education',
  'certifications',
  'softwareSkills',
  'volunteering',
  'references',
  'accomplishments',
  'additionalInfo'
];

const Resume24 = ({
  color = darkBlue,
  font = "inherit",
  fontSize = "14px",
  headingSize = "24px",
  fontStyle = "normal",
  sectionSpacing = 50,
  paragraphSpacing = 30,
  lineSpacing = 20,
  topBottomMargin = 40,
  sideMargins = 40,
  lineWeight = 1,
  pageSize = "A4",
  formData = {},
  photo = "",
  workExperiences = [],
  exporting = false,
  enableDragDrop = true,
  linkedIn = '',
}) => {
  const page = pageSizeMap[pageSize] || pageSizeMap.A4;
  
  // Merge default data with form data
  const data = { 
    ...defaultData, 
    ...formData,
    linkedIn: linkedIn || formData.linkedIn || '',
  };
  
  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState([]);
  const mainContentRef = useRef(null);
  const sidebarContentRef = useRef(null);
  
  const [educationEntries, setEducationEntries] = useState([]);
  const [savedSkills, setSavedSkills] = useState([]);
  const [savedSummary, setSavedSummary] = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});

  // State for section ordering
  const [leftSectionOrder, setLeftSectionOrder] = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSide, setDragOverSide] = useState(null);
  
  // State for arrange sections dialog
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // Unique instance id so injected scoped styles never collide with other resume instances on the same page
  const instanceIdRef = useRef(`r24-${Math.random().toString(36).slice(2)}`);
  const instanceClass = instanceIdRef.current;

  // Global style object for consistent font application
  const globalFontStyle = {
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
  };

  // Function to get text style with optional overrides
  const getTextStyle = (additionalStyles = {}) => ({
    ...globalFontStyle,
    ...additionalStyles,
  });

  // Heading style - only for headings
  const getHeadingStyle = (additionalStyles = {}) => ({
    fontFamily: font,
    fontSize: headingSize,
    fontStyle: fontStyle,
    fontWeight: 'bold',
    ...additionalStyles,
  });

  // Load data from localStorage
  useEffect(() => {
    const storedEntries = localStorage.getItem("educationEntries");
    if (storedEntries) {
      setEducationEntries(JSON.parse(storedEntries));
    }
    const storedSkills = localStorage.getItem("skills");
    if (storedSkills) {
      setSavedSkills(JSON.parse(storedSkills));
    }
    const storedSummaries = JSON.parse(localStorage.getItem("summaries")) || [];
    if (storedSummaries.length > 0) {
      setSavedSummary(storedSummaries[storedSummaries.length - 1]);
    }
    const storedAdditionalInfo = JSON.parse(localStorage.getItem("additionalInfo")) || [];
    setSavedAdditionalInfo(storedAdditionalInfo);
    const storedLanguages = JSON.parse(localStorage.getItem("languagesList")) || [];
    setSavedLanguages(storedLanguages);
    const storedAccomplishments = JSON.parse(localStorage.getItem("accomplishmentsList")) || [];
    setSavedAccomplishments(storedAccomplishments);
    const storedCertifications = JSON.parse(localStorage.getItem("certificationsList")) || [];
    setSavedCertifications(storedCertifications);
    const storedReferences = JSON.parse(localStorage.getItem("referencesList")) || [];
    setSavedReferences(storedReferences);
    const storedSoftwareSkills = JSON.parse(localStorage.getItem("softwareSkills")) || [];
    setSoftwareSkills(storedSoftwareSkills);
    const storedVolunteering = JSON.parse(localStorage.getItem("volunteering")) || [];
    setSavedVolunteering(storedVolunteering);
    const storedInterests = JSON.parse(localStorage.getItem("interests")) || [];
    setSavedInterests(storedInterests);
    const storedWebsites = JSON.parse(localStorage.getItem("websites")) || [];
    setSavedWebsites(storedWebsites);
    
    const userId = localStorage.getItem("userId");
    let sectionsData = {};

    if (userId) {
      const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`)) || [];
      
      if (currentCustomSections.length > 0) {
        currentCustomSections.forEach(sectionName => {
          const userCustomKey = `custom_${userId}_${sectionName}`;
          try {
            const customSectionData = localStorage.getItem(userCustomKey);
            if (customSectionData) {
              const parsedData = JSON.parse(customSectionData);
              const items = parsedData.items || [];
              const validItems = items.filter(item => item && item.trim() !== '');
              if (validItems.length > 0) {
                sectionsData[sectionName] = validItems;
              }
            }
          } catch (error) {
            console.error(`Error loading custom section ${sectionName}:`, error);
          }
        });
      }
    }

    setCustomSections(sectionsData);

    // Load saved section orders
    const savedLeftSectionOrder = localStorage.getItem("resume24LeftSectionOrder");
    if (savedLeftSectionOrder) {
      const parsedOrder = JSON.parse(savedLeftSectionOrder);
      const filteredOrder = parsedOrder.filter(section => !section.startsWith('custom_'));
      setLeftSectionOrder(filteredOrder);
    } else {
      setLeftSectionOrder(defaultLeftSectionOrder);
    }

    const savedRightSectionOrder = localStorage.getItem("resume24RightSectionOrder");
    if (savedRightSectionOrder) {
      const parsedOrder = JSON.parse(savedRightSectionOrder);
      const currentCustomSectionIds = Object.keys(sectionsData).map(name => `custom_${name}`);
      const filteredOrder = parsedOrder.filter(section => !section.startsWith('custom_'));
      const allRightSections = [...filteredOrder, ...currentCustomSectionIds];
      setRightSectionOrder(allRightSections);
    } else {
      const currentCustomSectionIds = Object.keys(sectionsData).map(name => `custom_${name}`);
      const updatedRightSectionOrder = [...defaultRightSectionOrder, ...currentCustomSectionIds];
      setRightSectionOrder(updatedRightSectionOrder);
    }
  }, []);

  // Auto-add/remove custom sections
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId && Object.keys(customSections).length > 0) {
      const currentRightOrder = [...rightSectionOrder];
      const currentCustomSectionIds = Object.keys(customSections).map(name => `custom_${name}`);
      
      const filteredRightOrder = currentRightOrder.filter(section => !section.startsWith('custom_'));
      const newRightOrder = [...filteredRightOrder, ...currentCustomSectionIds];
      
      if (JSON.stringify(newRightOrder) !== JSON.stringify(currentRightOrder)) {
        setRightSectionOrder(newRightOrder);
        localStorage.setItem("resume24RightSectionOrder", JSON.stringify(newRightOrder));
      }
      
      const currentLeftOrder = [...leftSectionOrder];
      const filteredLeftOrder = currentLeftOrder.filter(section => !section.startsWith('custom_'));
      if (JSON.stringify(filteredLeftOrder) !== JSON.stringify(currentLeftOrder)) {
        setLeftSectionOrder(filteredLeftOrder);
        localStorage.setItem("resume24LeftSectionOrder", JSON.stringify(filteredLeftOrder));
      }
    } else if (userId && Object.keys(customSections).length === 0) {
      const currentRightOrder = [...rightSectionOrder];
      const filteredRightOrder = currentRightOrder.filter(section => !section.startsWith('custom_'));
      if (filteredRightOrder.length !== currentRightOrder.length) {
        setRightSectionOrder(filteredRightOrder);
        localStorage.setItem("resume24RightSectionOrder", JSON.stringify(filteredRightOrder));
      }
      
      const currentLeftOrder = [...leftSectionOrder];
      const filteredLeftOrder = currentLeftOrder.filter(section => !section.startsWith('custom_'));
      if (filteredLeftOrder.length !== currentLeftOrder.length) {
        setLeftSectionOrder(filteredLeftOrder);
        localStorage.setItem("resume24LeftSectionOrder", JSON.stringify(filteredLeftOrder));
      }
    }
  }, [customSections]);

  // Save section orders to localStorage
  useEffect(() => {
    if (leftSectionOrder.length > 0) {
      localStorage.setItem("resume24LeftSectionOrder", JSON.stringify(leftSectionOrder));
    }
  }, [leftSectionOrder]);

  useEffect(() => {
    if (rightSectionOrder.length > 0) {
      localStorage.setItem("resume24RightSectionOrder", JSON.stringify(rightSectionOrder));
    }
  }, [rightSectionOrder]);

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  // Drag and drop handlers
  const handleLeftDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({
      index,
      side: 'left',
      sectionId: leftSectionOrder[index]
    }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleRightDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({
      index,
      side: 'right',
      sectionId: rightSectionOrder[index]
    }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragOver = (e, index, side) => {
    e.preventDefault();
    setDragOverIndex(index);
    setDragOverSide(side);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverIndex(null);
    setDragOverSide(null);
  };

  const handleLeftDrop = (e, targetIndex) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    const sourceIndex = data.index;
    const sourceSide = data.side;
    const sectionId = data.sectionId;

    if (sourceSide === 'left' && sourceIndex !== targetIndex) {
      const newSectionOrder = [...leftSectionOrder];
      const [movedSection] = newSectionOrder.splice(sourceIndex, 1);
      newSectionOrder.splice(targetIndex, 0, movedSection);
      setLeftSectionOrder(newSectionOrder);
    } else if (sourceSide === 'right') {
      const newLeftSectionOrder = [...leftSectionOrder];
      const newRightSectionOrder = [...rightSectionOrder];
      newRightSectionOrder.splice(sourceIndex, 1);
      newLeftSectionOrder.splice(targetIndex, 0, sectionId);
      setLeftSectionOrder(newLeftSectionOrder);
      setRightSectionOrder(newRightSectionOrder);
    }

    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleRightDrop = (e, targetIndex) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    const sourceIndex = data.index;
    const sourceSide = data.side;
    const sectionId = data.sectionId;

    if (sourceSide === 'right' && sourceIndex !== targetIndex) {
      const newRightSectionOrder = [...rightSectionOrder];
      const [movedSection] = newRightSectionOrder.splice(sourceIndex, 1);
      newRightSectionOrder.splice(targetIndex, 0, movedSection);
      setRightSectionOrder(newRightSectionOrder);
    } else if (sourceSide === 'left') {
      const newLeftSectionOrder = [...leftSectionOrder];
      const newRightSectionOrder = [...rightSectionOrder];
      newLeftSectionOrder.splice(sourceIndex, 1);
      newRightSectionOrder.splice(targetIndex, 0, sectionId);
      setLeftSectionOrder(newLeftSectionOrder);
      setRightSectionOrder(newRightSectionOrder);
    }

    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleDragEnd = (e) => {
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const resetLeftSectionOrder = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
  };

  const resetRightSectionOrder = () => {
    setRightSectionOrder(defaultRightSectionOrder);
  };

  const resetAllSectionOrder = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
    setRightSectionOrder(defaultRightSectionOrder);
  };

  const handleArrangeSectionsClick = () => {
    setArrangeDialogOpen(true);
  };

  const handleArrangeDialogClose = () => {
    setArrangeDialogOpen(false);
  };

  const renderDragPanel = (side, sections) => {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', ...getTextStyle() }}>
          <DragIndicator sx={{ mr: 1 }} />
          {side === 'left' ? 'Left Side' : 'Right Side'} Sections
          <Typography variant="body2" color="textSecondary" sx={{ ml: 1, ...getTextStyle() }}>
            ({sections.length} sections)
          </Typography>
        </Typography>
        <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
          {sections.length > 0 ? (
            sections.map((sectionId, index) => (
              <Paper
                key={sectionId}
                draggable
                onDragStart={(e) => side === 'left' ? handleLeftDragStart(e, index) : handleRightDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index, side)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => side === 'left' ? handleLeftDrop(e, index) : handleRightDrop(e, index)}
                onDragEnd={handleDragEnd}
                sx={{
                  p: 2,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: dragOverIndex === index && dragOverSide === side ? '#f0f0f0' : 'white',
                  boxShadow: dragOverIndex === index && dragOverSide === side ? 2 : 1,
                  cursor: 'grab',
                  '&:active': { cursor: 'grabbing' },
                  transition: 'all 0.2s ease',
                }}
              >
                <DragIndicator color="action" />
                <Typography sx={{ ml: 1, flexGrow: 1, ...getTextStyle() }}>
                  {getSectionTitle(sectionId)}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={getTextStyle()}>
                  {index + 1}
                </Typography>
              </Paper>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center', ...getTextStyle() }}>
              No sections. Drag sections from the other side.
            </Typography>
          )}
        </Box>
        <Button 
          onClick={side === 'left' ? resetLeftSectionOrder : resetRightSectionOrder} 
          variant="outlined" 
          size="small" 
          sx={{ mt: 1 }}
        >
          Reset {side === 'left' ? 'Left' : 'Right'} Order
        </Button>
      </Box>
    );
  };

  const getSectionTitle = (sectionId) => {
    const titles = {
      'aboutMe': 'About Me',
      'contact': 'Contact',
      'skills': 'Skills',
      'languages': 'Languages',
      'interests': 'Interests',
      'experience': 'Experience',
      'education': 'Education',
      'certifications': 'Certifications',
      'softwareSkills': 'Software Skills',
      'volunteering': 'Volunteering',
      'references': 'References',
      'accomplishments': 'Accomplishments',
      'additionalInfo': 'Additional Information'
    };
    
    if (sectionId.startsWith('custom_')) {
      return sectionId.replace('custom_', '').toUpperCase();
    }
    
    return titles[sectionId] || sectionId;
  };

  // Function to add font styles to HTML content
  const addFontStylesToHTML = (htmlContent) => {
    if (!htmlContent) return '';
    return htmlContent
      .replace(/<div/g, `<div style="font-family: ${font} !important; font-style: ${fontStyle} !important; font-size: ${fontSize} !important; line-height: ${lineSpacing}px !important;"`)
      .replace(/<p/g, `<p style="font-family: ${font} !important; font-style: ${fontStyle} !important; font-size: ${fontSize} !important; line-height: ${lineSpacing}px !important;"`)
      .replace(/<span/g, `<span style="font-family: ${font} !important; font-style: ${fontStyle} !important; font-size: ${fontSize} !important;"`)
      .replace(/<h1/g, `<h1 style="font-family: ${font} !important; font-style: ${fontStyle} !important; font-size: ${headingSize} !important;"`)
      .replace(/<h2/g, `<h2 style="font-family: ${font} !important; font-style: ${fontStyle} !important; font-size: ${headingSize} !important;"`)
      .replace(/<h3/g, `<h3 style="font-family: ${font} !important; font-style: ${fontStyle} !important; font-size: ${headingSize} !important;"`)
      .replace(/<h4/g, `<h4 style="font-family: ${font} !important; font-style: ${fontStyle} !important; font-size: ${headingSize} !important;"`)
      .replace(/<h5/g, `<h5 style="font-family: ${font} !important; font-style: ${fontStyle} !important; font-size: ${headingSize} !important;"`)
      .replace(/<h6/g, `<h6 style="font-family: ${font} !important; font-style: ${fontStyle} !important; font-size: ${headingSize} !important;"`)
      .replace(/<li/g, `<li style="font-family: ${font} !important; font-style: ${fontStyle} !important; font-size: ${fontSize} !important; line-height: ${lineSpacing}px !important;"`)
      .replace(/<ul/g, `<ul style="font-family: ${font} !important; font-style: ${fontStyle} !important;"`)
      .replace(/<ol/g, `<ol style="font-family: ${font} !important; font-style: ${fontStyle} !important;"`);
  };

  // Scoped CSS injected once per render, guaranteeing DesignFormatting props win over
  // MUI's class-based styles that otherwise survive dangerouslySetInnerHTML re-injection
  const scopedDesignFormattingCSS = `
    .${instanceClass} .resume-section,
    .${instanceClass} .resume-section * {
      font-family: ${font} !important;
      font-style: ${fontStyle} !important;
    }
    .${instanceClass} .resume-section p,
    .${instanceClass} .resume-section span,
    .${instanceClass} .resume-section li,
    .${instanceClass} .resume-section div:not([class*="MuiBox-root"]):not([class*="MuiGrid"]) {
      font-size: ${fontSize} !important;
      line-height: ${lineSpacing}px !important;
    }
    .${instanceClass} .resume-section h1,
    .${instanceClass} .resume-section h2,
    .${instanceClass} .resume-section h3,
    .${instanceClass} .resume-section h4,
    .${instanceClass} .resume-section h5,
    .${instanceClass} .resume-section h6,
    .${instanceClass} .resume-section .resume-heading {
      font-size: ${headingSize} !important;
      font-family: ${font} !important;
      font-style: ${fontStyle} !important;
    }
    .${instanceClass} .resume-section hr,
    .${instanceClass} .resume-section .MuiDivider-root {
      border-bottom-width: ${lineWeight}px !important;
      border-top-width: 0 !important;
    }
    .${instanceClass} .resume-sidebar-block {
      border-left-width: ${lineWeight}px !important;
    }
  `;

  // Language Proficiency Circle Component
  const LanguageProficiency = ({ name, proficiency }) => {
    const circleSize = 60;
    const strokeWidth = 5;
    const radius = (circleSize - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (proficiency / 100) * circumference;

    return (
      <Box sx={{ textAlign: 'center', mb: 1, px: 0.5 }}>
        <svg width={circleSize} height={circleSize} style={{ transform: 'rotate(-90deg)' }}>
          <circle
            stroke={lightGrayText}
            fill="transparent"
            strokeWidth={strokeWidth}
            r={radius}
            cx={circleSize / 2}
            cy={circleSize / 2}
          />
          <circle
            stroke="white"
            fill="transparent"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference + ' ' + circumference}
            style={{ strokeDashoffset: offset }}
            r={radius}
            cx={circleSize / 2}
            cy={circleSize / 2}
          />
        </svg>
        <Typography variant="caption" sx={{ color: 'white', display: 'block', mt: -3, ...getTextStyle() }}>
          {proficiency}%
        </Typography>
        <Typography variant="body2" sx={{ color: 'white', mt: 0.5, ...getTextStyle() }}>
          {name}
        </Typography>
      </Box>
    );
  };

  // Render left sidebar sections
  const renderLeftSectionContent = (sectionId) => {
    const sectionSpacingValue = `${sectionSpacing}px`;
    const paragraphSpacingValue = `${paragraphSpacing}px`;
    const headingColor = 'white';
    const textColor = lightGrayText;
    
    switch (sectionId) {
      case 'aboutMe':
        return (
          <div className="resume-section" style={{ marginBottom: sectionSpacingValue }}>
            <Typography variant="h6" className="resume-heading" sx={{ fontWeight: 'bold', mb: 1, textTransform: 'uppercase', color: headingColor, ...getHeadingStyle({ fontSize: `calc(${headingSize} * 0.8)` }) }}>
              About Me
            </Typography>
            <Typography variant="body2" sx={{ color: textColor, ...getTextStyle() }}>
              {savedSummary || data.profile}
            </Typography>
          </div>
        );
      
      case 'contact':
        return (
          <div className="resume-section" style={{ marginBottom: sectionSpacingValue }}>
            <Typography variant="h6" className="resume-heading" sx={{ fontWeight: 'bold', mb: 1, textTransform: 'uppercase', color: headingColor, ...getHeadingStyle({ fontSize: `calc(${headingSize} * 0.8)` }) }}>
              Contact
            </Typography>
            <List dense disablePadding>
              <ListItem disableGutters sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 30 }}><PhoneIcon sx={{ fontSize: 16, color: headingColor }} /></ListItemIcon>
                <ListItemText primary={data.phone} primaryTypographyProps={{ color: headingColor, ...getTextStyle() }} />
              </ListItem>
              <ListItem disableGutters sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 30 }}><MailOutlineIcon sx={{ fontSize: 16, color: headingColor }} /></ListItemIcon>
                <ListItemText primary={data.email} primaryTypographyProps={{ color: headingColor, ...getTextStyle() }} />
              </ListItem>
              {data.linkedIn && (
                <ListItem disableGutters sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 30 }}><LinkedInIcon sx={{ fontSize: 16, color: headingColor }} /></ListItemIcon>
                  <ListItemText primary={data.linkedIn} primaryTypographyProps={{ color: headingColor, ...getTextStyle() }} />
                </ListItem>
              )}
              <ListItem disableGutters sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 30 }}><LocationOnIcon sx={{ fontSize: 16, color: headingColor }} /></ListItemIcon>
                <ListItemText primary={data.city} primaryTypographyProps={{ color: headingColor, ...getTextStyle() }} />
              </ListItem>
            </List>
          </div>
        );
      
      case 'skills':
        return (
          <div className="resume-section" style={{ marginBottom: sectionSpacingValue }}>
            <Typography variant="h6" className="resume-heading" sx={{ fontWeight: 'bold', mb: 1, textTransform: 'uppercase', color: headingColor, ...getHeadingStyle({ fontSize: `calc(${headingSize} * 0.8)` }) }}>
              Skills
            </Typography>
            <List dense disablePadding>
              {(savedSkills.length > 0 ? savedSkills : data.skills).map((skill, index) => (
                <ListItem key={index} disableGutters sx={{ py: 0.3 }}>
                  <ListItemIcon sx={{ minWidth: 30 }}><CircleIcon sx={{ fontSize: 6, color: headingColor }} /></ListItemIcon>
                  <ListItemText 
                    primary={typeof skill === 'object' ? skill.name : skill} 
                    primaryTypographyProps={{ color: headingColor, ...getTextStyle() }} 
                  />
                </ListItem>
              ))}
            </List>
          </div>
        );
      
      case 'languages':
        return savedLanguages.length > 0 ? (
          <div className="resume-section" style={{ marginBottom: sectionSpacingValue }}>
            <Typography variant="h6" className="resume-heading" sx={{ fontWeight: 'bold', mb: 1, textTransform: 'uppercase', color: headingColor, ...getHeadingStyle({ fontSize: `calc(${headingSize} * 0.8)` }) }}>
              Language
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              {savedLanguages.map((lang, index) => (
                <LanguageProficiency key={index} name={lang.name} proficiency={lang.level || 70} />
              ))}
            </Box>
          </div>
        ) : null;
      
      case 'interests':
        return savedInterests.length > 0 ? (
          <div className="resume-section" style={{ marginBottom: sectionSpacingValue }}>
            <Typography variant="h6" className="resume-heading" sx={{ fontWeight: 'bold', mb: 1, textTransform: 'uppercase', color: headingColor, ...getHeadingStyle({ fontSize: `calc(${headingSize} * 0.8)` }) }}>
              Interests
            </Typography>
            {savedInterests.map((interest, index) => (
              <Typography key={index} sx={{ color: headingColor, mb: 0.5, ...getTextStyle() }}>
                • {interest}
              </Typography>
            ))}
          </div>
        ) : null;
      
      default:
        if (sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = customSections[customSectionName] || [];
          return items.length > 0 ? (
            <div className="resume-section" style={{ marginBottom: sectionSpacingValue }}>
              <Typography variant="h6" className="resume-heading" sx={{ fontWeight: 'bold', mb: 1, textTransform: 'uppercase', color: headingColor, ...getHeadingStyle({ fontSize: `calc(${headingSize} * 0.8)` }) }}>
                {customSectionName.toUpperCase()}
              </Typography>
              <List dense disablePadding>
                {items.map((item, i) => (
                  <ListItem key={i} disableGutters sx={{ py: 0.3 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}><CircleIcon sx={{ fontSize: 6, color: headingColor }} /></ListItemIcon>
                    <ListItemText 
                      primary={item}
                      primaryTypographyProps={{ color: headingColor, ...getTextStyle() }} 
                    />
                  </ListItem>
                ))}
              </List>
            </div>
          ) : null;
        }
        return null;
    }
  };

  // Render right main content sections
  const renderRightSectionContent = (sectionId) => {
    const textColor = secondaryText;
    const headingColor = color;
    const sectionSpacingValue = `${sectionSpacing}px`;
    const paragraphSpacingValue = `${paragraphSpacing}px`;
    const dividerBorderStyle = { borderColor: '#e0e0e0', borderBottomWidth: `${lineWeight}px` };
    
    switch (sectionId) {
      case 'experience':
        return (
          <div className="resume-section" style={{ marginBottom: sectionSpacingValue }}>
            <Typography variant="h5" className="resume-heading" sx={{ fontWeight: 'bold', color: headingColor, mb: 2, textTransform: 'uppercase', ...getHeadingStyle() }}>
              Experience
            </Typography>
            {workExperiences && workExperiences.length > 0 ? (
              workExperiences.map((exp, index) => (
                <Box key={index} sx={{ mb: paragraphSpacingValue }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: headingColor, ...getTextStyle() }}>
                      {exp.employer}
                    </Typography>
                    <Typography variant="caption" sx={{ color: textColor, ...getTextStyle() }}>
                      {formatDate(exp.startMonth, exp.startYear)} - {exp.current ? "Present" : formatDate(exp.endMonth, exp.endYear)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: textColor, mb: 1, ...getTextStyle() }}>
                    {exp.jobTitle}
                  </Typography>
                  {exp.description && (
                    <Box
                      sx={{ 
                        mt: 1,
                        ...getTextStyle(),
                        color: textColor,
                        "& strong": { fontWeight: "bold" }, 
                        "& em": { fontStyle: "italic" }, 
                        "& u": { textDecoration: "underline" } 
                      }}
                      dangerouslySetInnerHTML={{
                        __html: exp.description
                          .replace(/\*\*(.*?)\*\*/g, `<strong style="font-family: ${font} !important; font-size: ${fontSize} !important; font-weight: bold; color: ${textColor}">$1</strong>`)
                          .replace(/\*(.*?)\*/g, `<em style="font-family: ${font} !important; font-size: ${fontSize} !important; font-style: italic; color: ${textColor}">$1</em>`)
                          .replace(/<u>(.*?)<\/u>/g, `<u style="font-family: ${font} !important; font-size: ${fontSize} !important; color: ${textColor}">$1</u>`)
                          .replace(/\n/g, `<br />`),
                      }}
                    />
                  )}
                </Box>
              ))
            ) : (
              <Typography sx={{ ...getTextStyle(), color: textColor }}>No work experiences added yet.</Typography>
            )}
          </div>
        );
      
      case 'education':
        return educationEntries.length > 0 ? (
          <div className="resume-section" style={{ marginBottom: sectionSpacingValue }}>
            <Divider sx={{ my: 2, ...dividerBorderStyle }} />
            <Typography variant="h5" className="resume-heading" sx={{ fontWeight: 'bold', color: headingColor, mb: 2, textTransform: 'uppercase', ...getHeadingStyle() }}>
              Education
            </Typography>
            {educationEntries.map((edu, index) => {
              if (edu.educationLevel) {
                if (edu.educationLevel === "school") {
                  return (
                    <Box key={index} sx={{ mb: paragraphSpacingValue }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: headingColor, ...getTextStyle() }}>
                          {`${edu.standard} Standard`} {edu.stream ? `- ${edu.stream}` : ''}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: textColor, ...getTextStyle() }}>
                        {edu.schoolName}
                      </Typography>
                      {(edu.schoolLocation || edu.percentage || edu.district) && (
                        <Typography variant="caption" sx={{ color: textColor, mt: 0.5, display: 'block', ...getTextStyle() }}>
                          {[edu.schoolLocation, edu.percentage ? `${edu.percentage}%` : ''].filter(item => item).join(' • ')}
                        </Typography>
                      )}
                    </Box>
                  );
                } else {
                  return (
                    <Box key={index} sx={{ mb: paragraphSpacingValue }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: headingColor, ...getTextStyle() }}>
                          {`${edu.degreeName}`} {edu.specialization ? `- ${edu.specialization}` : ''}
                        </Typography>
                        <Typography variant="caption" sx={{ color: textColor, ...getTextStyle() }}>
                          {formatDate(edu.gradMonth, edu.gradYear)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: textColor, ...getTextStyle() }}>
                        {edu.collegeName}
                      </Typography>
                      {(edu.collegeLocation || edu.cgpa || edu.grade || edu.programType) && (
                        <Typography variant="caption" sx={{ color: textColor, mt: 0.5, display: 'block', ...getTextStyle() }}>
                          {[edu.collegeLocation, edu.cgpa ? `CGPA: ${edu.cgpa}` : '', edu.grade ? `Grade: ${edu.grade}` : '', edu.programType].filter(item => item).join(' • ')}
                        </Typography>
                      )}
                    </Box>
                  );
                }
              } else {
                return (
                  <Box key={index} sx={{ mb: paragraphSpacingValue }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: headingColor, ...getTextStyle() }}>
                        {edu.institution || edu.schoolName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: textColor, ...getTextStyle() }}>
                        {edu.years || formatDate(edu.gradMonth, edu.gradYear)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: textColor, ...getTextStyle() }}>
                      {edu.degree} {edu.fieldOfStudy ? `- ${edu.fieldOfStudy}` : ''}
                    </Typography>
                  </Box>
                );
              }
            })}
          </div>
        ) : null;
      
      case 'certifications':
        return savedCertifications.length > 0 ? (
          <div className="resume-section" style={{ marginBottom: sectionSpacingValue }}>
            <Divider sx={{ my: 2, ...dividerBorderStyle }} />
            <Box sx={{ mb: 2 }}>
              <Typography variant="h5" className="resume-heading" sx={{ fontWeight: 'bold', color: headingColor, mb: 2, textTransform: 'uppercase', ...getHeadingStyle() }}>
                Certifications
              </Typography>
              {savedCertifications.map((cert, index) => (
                <Box key={index} sx={{ mb: paragraphSpacingValue }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: headingColor, ...getTextStyle() }}>
                    {cert.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: textColor, mb: 0.5, ...getTextStyle() }}>
                    {cert.provider} • {cert.year}
                  </Typography>
                  {cert.description && (
                    <Typography variant="body2" sx={{ ...getTextStyle(), color: textColor }}>
                      {cert.description}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </div>
        ) : null;

      case 'softwareSkills':
        return softwareSkills.length > 0 ? (
          <div className="resume-section" style={{ marginBottom: sectionSpacingValue }}>
            <Divider sx={{ my: 2, ...dividerBorderStyle }} />
            <Box sx={{ mb: 2 }}>
              <Typography variant="h5" className="resume-heading" sx={{ fontWeight: 'bold', color: headingColor, mb: 2, textTransform: 'uppercase', ...getHeadingStyle() }}>
                Software Skills
              </Typography>
              <Grid container spacing={1}>
                {softwareSkills.map((skill, index) => (
                  <Grid item xs={6} key={index}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ ...getTextStyle(), color: textColor }}>
                        {skill.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ 
                          width: '60px', 
                          height: '6px', 
                          backgroundColor: '#e0e0e0',
                          borderRadius: '3px',
                          overflow: 'hidden',
                          mr: 1,
                        }}>
                          <Box 
                            sx={{ 
                              width: `${skill.level}%`, 
                              height: '100%', 
                              backgroundColor: headingColor,
                            }} 
                          />
                        </Box>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', ...getTextStyle(), color: textColor }}>
                          {skill.level}%
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </div>
        ) : null;
      
      case 'volunteering':
        return savedVolunteering.length > 0 ? (
          <div className="resume-section" style={{ marginBottom: sectionSpacingValue }}>
            <Divider sx={{ my: 2, ...dividerBorderStyle }} />
            <Box sx={{ mb: 2 }}>
              <Typography variant="h5" className="resume-heading" sx={{ fontWeight: 'bold', color: headingColor, mb: 2, textTransform: 'uppercase', ...getHeadingStyle() }}>
                Volunteering
              </Typography>
              {savedVolunteering.map((vol, index) => (
                <Box key={index} sx={{ mb: paragraphSpacingValue }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: headingColor, ...getTextStyle() }}>
                      {vol.subtopic}
                    </Typography>
                    <Typography variant="caption" sx={{ color: textColor, ...getTextStyle() }}>
                      {vol.fromDate} - {vol.toDate}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-line", ...getTextStyle(), color: textColor }}>
                    {vol.content}
                  </Typography>
                </Box>
              ))}
            </Box>
          </div>
        ) : null;
      
      case 'references':
        return (savedReferences.length > 0 || data.references.length > 0) ? (
          <div className="resume-section" style={{ marginBottom: sectionSpacingValue }}>
            <Divider sx={{ my: 2, ...dividerBorderStyle }} />
            <Typography variant="h5" className="resume-heading" sx={{ fontWeight: 'bold', color: headingColor, mb: 2, textTransform: 'uppercase', ...getHeadingStyle() }}>
              References
            </Typography>
            <Grid container spacing={2}>
              {(savedReferences.length > 0 ? savedReferences : data.references).map((ref, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', ...getTextStyle(), color: headingColor }}>
                    {typeof ref === 'object' ? ref.name : ref}
                  </Typography>
                  {typeof ref === 'object' && (
                    <>
                      <Typography variant="body2" sx={{ color: textColor, ...getTextStyle() }}>
                        {ref.position} @ {ref.company}
                      </Typography>
                      <Typography variant="body2" sx={{ color: textColor, ...getTextStyle() }}>
                        Phone: {ref.phone}
                      </Typography>
                      <Typography variant="body2" sx={{ color: textColor, ...getTextStyle() }}>
                        Email: {ref.email}
                      </Typography>
                    </>
                  )}
                </Grid>
              ))}
            </Grid>
          </div>
        ) : null;
      
      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <div className="resume-section" style={{ marginBottom: sectionSpacingValue }}>
            <Divider sx={{ my: 2, ...dividerBorderStyle }} />
            <Box sx={{ mb: 2 }}>
              <Typography variant="h5" className="resume-heading" sx={{ fontWeight: 'bold', color: headingColor, mb: 2, textTransform: 'uppercase', ...getHeadingStyle() }}>
                Accomplishments
              </Typography>
              {savedAccomplishments.map((acc, index) => (
                <Typography key={index} sx={{ mb: `${paragraphSpacing / 10}px`, ...getTextStyle(), color: textColor }} whiteSpace="pre-line">
                  • {acc}
                </Typography>
              ))}
            </Box>
          </div>
        ) : null;
      
      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <div className="resume-section" style={{ marginBottom: sectionSpacingValue }}>
            <Divider sx={{ my: 2, ...dividerBorderStyle }} />
            <Box sx={{ mb: 2 }}>
              <Typography variant="h5" className="resume-heading" sx={{ fontWeight: 'bold', color: headingColor, mb: 2, textTransform: 'uppercase', ...getHeadingStyle() }}>
                Additional Information
              </Typography>
              {savedAdditionalInfo.map((info, index) => (
                <Typography key={index} sx={{ mb: `${paragraphSpacing / 10}px`, ...getTextStyle(), color: textColor }}>
                  {info}
                </Typography>
              ))}
            </Box>
          </div>
        ) : null;
      
      default:
        if (sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = customSections[customSectionName] || [];
          
          if (items && items.length > 0) {
            return (
              <div className="resume-section" style={{ marginBottom: sectionSpacingValue }}>
                <Divider sx={{ my: 2, ...dividerBorderStyle }} />
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h5" className="resume-heading" sx={{ fontWeight: 'bold', color: headingColor, mb: 2, textTransform: 'uppercase', ...getHeadingStyle() }}>
                    {customSectionName.toUpperCase()}
                  </Typography>
                  {items.map((item, i) => (
                    <Typography key={i} sx={{ mb: `${paragraphSpacing / 10}px`, ...getTextStyle(), color: textColor }} whiteSpace="pre-line">
                      • {item}
                    </Typography>
                  ))}
                </Box>
              </div>
            );
          }
          return null;
        }
        return null;
    }
  };

  // Pagination function that properly splits content across pages
  const paginateContent = useCallback(() => {
    if (!mainContentRef.current || !sidebarContentRef.current) return;

    const mainSections = Array.from(mainContentRef.current.querySelectorAll('.resume-section'));
    const sidebarSections = Array.from(sidebarContentRef.current.querySelectorAll('.resume-section'));
    
    const pageHeightInPx = page.heightPx;
    const availableHeight = pageHeightInPx - (topBottomMargin * 2);

    let newPages = [];
    let currentMainPageSections = [];
    let currentSidebarPageSections = [];
    let currentMainHeight = 0;
    
    if (mainSections.length === 0 && sidebarSections.length === 0) {
      newPages.push({ main: [], sidebar: [] });
    }
    
    let sidebarIndex = 0;
    
    mainSections.forEach((section) => {
      const sectionHeight = section.offsetHeight;
      
      if (currentMainHeight + sectionHeight > availableHeight && currentMainPageSections.length > 0) {
        const pageSidebarSections = [];
        let currentSidebarHeight = 0;
        
        while (sidebarIndex < sidebarSections.length) {
          const sidebarSection = sidebarSections[sidebarIndex];
          const sidebarHeight = sidebarSection.offsetHeight;
          
          if (currentSidebarHeight + sidebarHeight <= availableHeight) {
            pageSidebarSections.push(sidebarSection);
            currentSidebarHeight += sidebarHeight;
            sidebarIndex++;
          } else {
            break;
          }
        }
        
        newPages.push({
          main: [...currentMainPageSections],
          sidebar: pageSidebarSections,
        });
        
        currentMainPageSections = [section];
        currentMainHeight = sectionHeight;
      } else {
        currentMainPageSections.push(section);
        currentMainHeight += sectionHeight;
      }
    });
    
    const remainingSidebarSections = sidebarSections.slice(sidebarIndex);
    
    if (currentMainPageSections.length > 0 || remainingSidebarSections.length > 0) {
      newPages.push({
        main: currentMainPageSections,
        sidebar: remainingSidebarSections,
      });
    }
    
    if (newPages.length === 0) {
      newPages.push({ main: [], sidebar: [] });
    }
    
    setPages(newPages);
  }, [page.heightPx, topBottomMargin]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      paginateContent();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    paginateContent,
    educationEntries,
    savedSkills,
    savedSummary,
    workExperiences,
    savedAdditionalInfo,
    savedLanguages,
    savedAccomplishments,
    savedCertifications,
    savedReferences,
    softwareSkills,
    savedVolunteering,
    savedInterests,
    savedWebsites,
    customSections,
    leftSectionOrder,
    rightSectionOrder,
    sectionSpacing,
    paragraphSpacing,
    fontSize,
    headingSize,
    lineSpacing,
    topBottomMargin,
    sideMargins,
  ]);

  // Hidden measurement containers
  const renderSidebarContentToMeasure = () => (
    <Box ref={sidebarContentRef} className={instanceClass} sx={{ visibility: 'hidden', position: 'absolute', top: 0, left: 0, width: `${page.widthPx * 0.3}px`, zIndex: -1 }}>
      <Box sx={{ 
        pt: `${topBottomMargin}px`,
        pb: `${topBottomMargin}px`,
        pl: `${sideMargins}px`,
        pr: `${sideMargins}px`,
        bgcolor: color, 
        color: 'white', 
        height: 'auto',
        borderBottomLeftRadius: '50px', 
        borderBottomRightRadius: { xs: '0', sm: '50px' },
        borderTopLeftRadius: '5px',
        ...getTextStyle(),
      }}>
        {leftSectionOrder.map(sectionId => (
          <React.Fragment key={sectionId}>
            {renderLeftSectionContent(sectionId)}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );

  const renderMainContentToMeasure = () => (
    <Box ref={mainContentRef} className={instanceClass} sx={{ visibility: 'hidden', position: 'absolute', top: 0, left: 0, width: `${page.widthPx * 0.7}px`, zIndex: -1 }}>
      <Box sx={{ 
        pt: `${topBottomMargin}px`,
        pb: `${topBottomMargin}px`,
        pl: `${sideMargins}px`,
        pr: `${sideMargins}px`,
        ...getTextStyle() 
      }}>
        {/* Name & Title Section */}
        <div className="resume-section" style={{ marginBottom: `${sectionSpacing}px` }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <Box>
              <Typography variant="h3" className="resume-heading" sx={{ fontWeight: '900', color: color, lineHeight: 1.1, ...getHeadingStyle({ fontSize: `calc(${headingSize} * 1.5)` }) }}>
                {data.firstName} {data.lastName}
              </Typography>
              <Typography variant="h6" sx={{ color: secondaryText, mb: 2, ...getTextStyle({ fontSize: `calc(${fontSize} * 1.1)` }) }}>
                {data.currentPosition}
              </Typography>
            </Box>
            {photo && (
              <Box sx={{ 
                width: '150px', 
                height: '120px', 
                borderRadius: '90px / 120px', 
                overflow: 'hidden', 
                ml: 3, 
                mb: 3,
                boxShadow: 3,
              }}>
                <Avatar 
                  src={photo}
                  sx={{ 
                    width: '100%', 
                    height: '100%', 
                    borderRadius: '0',
                    objectFit: 'cover'
                  }} 
                />
              </Box>
            )}
          </Box>
        </div>

        {rightSectionOrder.map(sectionId => (
          <React.Fragment key={sectionId}>
            {renderRightSectionContent(sectionId)}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );

  const renderSidebarContent = (sidebarSections = []) => {
    if (sidebarSections.length === 0) {
      return (
        <Box sx={{ 
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}>
          <Box sx={{ flexGrow: 1 }} />
        </Box>
      );
    }

    return (
      <Box display="flex" flexDirection="column" sx={{ 
        flexGrow: 1,
        minHeight: "100%",
        justifyContent: "flex-start",
      }}>
        {sidebarSections.map((section, index) => (
          <div key={index} dangerouslySetInnerHTML={{ __html: addFontStylesToHTML(section.outerHTML) }} />
        ))}
      </Box>
    );
  };

  return (
    <Box className={instanceClass} sx={{ width: "100%", position: "relative" }}>
      {/* Scoped style tag enforcing DesignFormatting props over MUI class styles re-injected via dangerouslySetInnerHTML */}
      <style>{scopedDesignFormattingCSS}</style>

      {!exporting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap', '@media print': { display: 'none' } }}>
          <IconButton onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0} color="primary"><ChevronLeft /></IconButton>
          <Typography sx={{ mx: 1 }}>Page {currentPage + 1} of {Math.max(1, pages.length)}</Typography>
          <IconButton onClick={() => setCurrentPage(prev => Math.min(pages.length - 1, prev + 1))} disabled={currentPage === Math.max(0, pages.length - 1)} color="primary"><ChevronRight /></IconButton>
          
          {enableDragDrop && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button 
                onClick={handleArrangeSectionsClick}
                variant="contained" 
                startIcon={<Reorder />}
                color="primary"
                size="small"
              >
                Arrange Sections
              </Button>
              <Button onClick={resetAllSectionOrder} variant="outlined" color="secondary" size="small">
                Reset All
              </Button>
            </Box>
          )}
        </Box>
      )}
      
      {renderMainContentToMeasure()}
      {renderSidebarContentToMeasure()}
      
      <Box id="resume-container">
        {pages.length > 0 ? (
          pages.map((pageContent, index) => (
            <Box
              key={index}
              className="resume-page"
              sx={{
                width: page.width,
                height: page.height,
                minHeight: page.height,
                mx: "auto",
                bgcolor: "#fff",
                boxShadow: index === currentPage ? 3 : 0,
                display: "flex",
                flexDirection: "column",
                pageBreakAfter: "always",
                overflow: "hidden",
                "@media print": {
                  width: page.width,
                  height: page.height,
                  minHeight: page.height,
                  boxShadow: "none",
                  m: 0,
                },
                display: exporting ? 'flex' : (index === currentPage ? 'flex' : 'none'),
                '@media print': { display: 'flex' }
              }}
            >
              <Grid container sx={{ height: "100%", margin: 0 }}>
                {/* Sidebar Column */}
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    width: "30%",
                    height: "100%",
                    margin: 0,
                    padding: 0,
                    "@media print": {
                      width: "30%",
                      height: "100%",
                      bgcolor: color,
                      color: "white",
                      WebkitPrintColorAdjust: "exact",
                      printColorAdjust: "exact",
                      margin: 0,
                      padding: 0,
                    },
                  }}
                >
                  <Box className="resume-sidebar-block" sx={{ 
                    backgroundColor: color,
                    color: 'white',
                    pt: `${topBottomMargin}px`,
                    pb: `${topBottomMargin}px`,
                    pl: `${sideMargins}px`,
                    pr: `${sideMargins}px`,
                    height: "100%",
                    margin: 0,
                    borderBottomLeftRadius: '50px', 
                    borderBottomRightRadius: { xs: '0', sm: '50px' },
                    borderTopLeftRadius: '5px',
                    display: "flex",
                    flexDirection: "column",
                    ...getTextStyle(),
                    "@media print": {
                      bgcolor: color,
                      color: "white",
                      WebkitPrintColorAdjust: "exact",
                      printColorAdjust: "exact",
                      height: "100%",
                      borderBottomLeftRadius: '50px', 
                      borderBottomRightRadius: '50px',
                      borderTopLeftRadius: '5px',
                      margin: 0,
                    },
                  }}>
                    {renderSidebarContent(pageContent.sidebar)}
                  </Box>
                </Grid>

                {/* Main Content Column */}
                <Grid
                  item
                  xs={12}
                  md={8}
                  sx={{
                    pt: `${topBottomMargin}px`,
                    pb: `${topBottomMargin}px`,
                    pl: `${sideMargins}px`,
                    pr: `${sideMargins}px`,
                    width: "70%",
                    height: "100%",
                    margin: 0,
                    "@media print": { 
                      pt: `${topBottomMargin}px`,
                      pb: `${topBottomMargin}px`,
                      pl: `${sideMargins}px`,
                      pr: `${sideMargins}px`,
                      height: "100%",
                      margin: 0,
                      overflow: "visible",
                    },
                  }}
                >
                  <div 
                    style={{ 
                      fontFamily: font, 
                      fontStyle: fontStyle, 
                      fontSize: fontSize,
                      lineHeight: `${lineSpacing}px`
                    }}
                    dangerouslySetInnerHTML={{ 
                      __html: addFontStylesToHTML(pageContent.main.map(s => s.outerHTML).join('')) 
                    }} 
                  />
                </Grid>
              </Grid>
            </Box>
          ))
        ) : (
          <Box
            className="resume-page"
            sx={{
              width: page.width,
              height: page.height,
              minHeight: page.height,
              mx: "auto",
              bgcolor: "#fff",
              boxShadow: 3,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              "@media print": {
                width: page.width,
                height: page.height,
                minHeight: page.height,
                boxShadow: "none",
                m: 0,
              },
            }}
          >
            <Grid container sx={{ height: "100%", margin: 0 }}>
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  width: "30%",
                  height: "100%",
                  margin: 0,
                  padding: 0,
                }}
              >
                <Box className="resume-sidebar-block" sx={{ 
                  backgroundColor: color,
                  color: 'white',
                  pt: `${topBottomMargin}px`,
                  pb: `${topBottomMargin}px`,
                  pl: `${sideMargins}px`,
                  pr: `${sideMargins}px`,
                  height: "100%",
                  margin: 0,
                  borderBottomLeftRadius: '50px', 
                  borderBottomRightRadius: { xs: '0', sm: '50px' },
                  borderTopLeftRadius: '5px',
                  display: "flex",
                  flexDirection: "column",
                  ...getTextStyle(),
                  "@media print": {
                    bgcolor: color,
                    color: "white",
                    WebkitPrintColorAdjust: "exact",
                    printColorAdjust: "exact",
                    height: "100%",
                    margin: 0,
                  },
                }}>
                  {renderSidebarContent()}
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                md={8}
                sx={{
                  pt: `${topBottomMargin}px`,
                  pb: `${topBottomMargin}px`,
                  pl: `${sideMargins}px`,
                  pr: `${sideMargins}px`,
                  width: "70%",
                  height: "100%",
                  margin: 0,
                  "@media print": { 
                    pt: `${topBottomMargin}px`,
                    pb: `${topBottomMargin}px`,
                    pl: `${sideMargins}px`,
                    pr: `${sideMargins}px`,
                    height: "100%",
                    margin: 0,
                  },
                }}
              >
                <Typography sx={getTextStyle()}>Loading resume...</Typography>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>

      {/* Arrange Sections Dialog */}
      <Dialog
        open={arrangeDialogOpen}
        onClose={handleArrangeDialogClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Reorder sx={{ mr: 1 }} />
            Arrange Resume Sections
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3, ...getTextStyle() }}>
            Drag and drop sections to reorder them within each side or move sections between left and right sides.
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
              width: "100%",
            }}
          >
            <Box sx={{ flex: 1, width: { xs: "100%", md: "50%" } }}>
              {renderDragPanel("left", leftSectionOrder)}
            </Box>

            <Box sx={{ flex: 1, width: { xs: "100%", md: "50%" } }}>
              {renderDragPanel("right", rightSectionOrder)}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={resetAllSectionOrder} color="secondary">
            Reset All
          </Button>
          <Button onClick={handleArrangeDialogClose} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume24;